import type { SceneGroup } from '@lumen5/luminary';

import { partitionTranscriptByScene } from './transcript-partition';

import { COMPACT_NARRATION_ABOVE_WORDS, fencePosts } from 'js/src/agent/video-state/fencePosts';
import type { RootState } from 'js/src/libs/RootState';
import { isAIAvatarSceneGroup, isVoiceoverSceneGroup } from 'js/src/luminary/luminary-selectors';

const MIN_GAP_DURATION_MILLIS = 10;
/** A gap this short is fixed by extending an overlay beside it, so the report names them. */
const MAX_SHORT_GAP_TOKENS = 2;
const NEIGHBOUR_WORDS_SHOWN = 4;

export interface GapNeighbour {
    sceneId: string;
    /** The neighbour's words nearest the gap. */
    words: string[];
}

export interface UncoveredTranscriptRange {
    startMillis: number;
    endMillis: number;
    words: string[];
    before?: GapNeighbour;
    after?: GapNeighbour;
}

/**
 * Finds transcript time ranges in a voiceover scene group that are not covered
 * by an overlay scene. In a voice-driven scene group, scenes tile the timeline,
 * so every non-overlay (base) scene is an uncovered range where the waveform
 * base is visible.
 *
 * Returns null when coverage does not apply: talking-head and AI-avatar scene
 * groups (their base visuals are fine to show) and groups without a transcript.
 */
export function getUncoveredTranscriptRanges(
    state: RootState,
    sceneGroup: SceneGroup,
): UncoveredTranscriptRange[] | null {
    if (!isVoiceoverSceneGroup(sceneGroup) || isAIAvatarSceneGroup(sceneGroup)) {
        return null;
    }
    const partition = partitionTranscriptByScene(state, sceneGroup);
    if (!partition) {
        return null;
    }

    // A coverage gap is simply a base ("uncovered") scene in the partition. Skip sub-10ms slivers
    // that arise from floating-point noise between adjacent overlays — but only when the sliver owns
    // no transcript words.
    return partition.flatMap((entry, index) => {
        const isGap =
            entry.role === 'uncovered' &&
            (entry.words.length > 0 ||
                entry.endMillis - entry.startMillis >= MIN_GAP_DURATION_MILLIS);
        if (!isGap) {
            return [];
        }
        const range: UncoveredTranscriptRange = {
            startMillis: entry.startMillis,
            endMillis: entry.endMillis,
            words: entry.words,
        };
        if (entry.words.length === 0 || entry.words.length > MAX_SHORT_GAP_TOKENS) {
            return [range];
        }
        const previous = partition[index - 1];
        const next = partition[index + 1];
        return [
            {
                ...range,
                ...(previous?.role === 'overlay' && {
                    before: {
                        sceneId: previous.scene.id,
                        words: previous.words.slice(-NEIGHBOUR_WORDS_SHOWN),
                    },
                }),
                ...(next?.role === 'overlay' && {
                    after: {
                        sceneId: next.scene.id,
                        words: next.words.slice(0, NEIGHBOUR_WORDS_SHOWN),
                    },
                }),
            },
        ];
    });
}

function describeNeighbours(
    { before, after }: UncoveredTranscriptRange,
    displaySceneId: (sceneId: string) => string,
): string {
    const beforeText =
        before && `scene ${displaySceneId(before.sceneId)} ("…${before.words.join(' ')}")`;
    const afterText =
        after && `scene ${displaySceneId(after.sceneId)} ("${after.words.join(' ')}…")`;
    if (beforeText && afterText) {
        return `, between ${beforeText} and ${afterText}`;
    }
    if (beforeText) {
        return `, after ${beforeText}`;
    }
    return afterText ? `, before ${afterText}` : '';
}

export function formatUncoveredRanges(
    ranges: UncoveredTranscriptRange[],
    displaySceneId: (sceneId: string) => string,
): string {
    if (ranges.length === 0) {
        return 'Full transcript coverage — no gaps remain.';
    }
    const lines = ranges.map((range) => {
        const text = range.words.length > 0 ? `"${fencePosts(range.words)}"` : '(no spoken words)';
        const start = (range.startMillis / 1000).toFixed(1);
        const end = (range.endMillis / 1000).toFixed(1);
        // A long gap is named by its ends: its words are already in full on the base scene covering it.
        const count =
            range.words.length > COMPACT_NARRATION_ABOVE_WORDS
                ? `, ${range.words.length.toLocaleString('en-US')} words`
                : '';
        return `  - ${text} (${start}s–${end}s${count})${describeNeighbours(range, displaySceneId)}`;
    });
    return `Uncovered transcript ranges remain — the viewer sees a bare waveform placeholder here instead which will look bad.\n${lines.join('\n')}`;
}
